import { useEffect, useCallback, useState } from "react";
import { useDarkMode } from "../../../../../context/ThemeContext";
import { useIsReseller } from "../../../../../hooks/useIsReseller";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import Swal from "sweetalert2";
import CustomAlert from "../../../../../components/base/CustomAlert";

import { useDeviceForm } from "./hooks";
import {
  DeviceHeader,
  DeviceList,
  DevicePagination,
  DeviceDetailsModal,
  DeviceEditModal,
  DeviceAddModal,
  DeviceTransferModal,
  DeviceBulkTransferModal,
} from "./components";

import type { ThemeClasses, Device } from "./types";
import { useMultiSelect, BulkActionBar, type BulkAction } from "../../../../reseller-admin/shared";
import { useCompaniesStore } from "../../../../../store/admin/companiesStore";
import {
  transferDevice,
  transferDevicesBulk,
  type TransferBackup,
  type TransferStatusPolicy,
} from "../../../../../features/devices/mutations";
import { getError } from "../../../../../utils/getError";
import { useVehiclesSocketStore } from "../../../../../store/admin/vehiclesSocketStore";
import { fetchDevicesFromAPI } from "../../../../../hooks/useVehiclesSocketSubscription";

export default function DevicesSection() {
  const { t } = useTranslation("adminSections");
  const { isDarkMode } = useDarkMode();
  const { isReseller } = useIsReseller();

  // Cleanup scroll lock on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const {
    devices,
    allBrands,
    allBrandsLoading,
    meta,
    loading,
    availableVehicles,
    availableVehiclesLoading,
    availableTrailers,
    availableTrailersLoading,
    companyId,
    selectedDevice,
    editForm,
    newDeviceForm,
    alert,
    isSubmitting,
    isModalOpen,
    isAddDeviceModalOpen,
    modalType,
    selectedStatus,
    selectedType,
    searchQuery,
    currentPage,
    selectedView,
    itemsPerPage,
    handleStatusChange,
    handleTypeChange,
    handleSearchChange,
    handleEditClick,
    handleDetailsClick,
    handleInputChange,
    handleSaveChanges,
    handleAddDevice,
    handleActivateDevice,
    handleDeactivateDevice,
    handleDeleteDevice,
    setCurrentPage,
    setSelectedView,
    closeModal,
    closeAddModal,
    openAddModal,
    loadingAction,
  } = useDeviceForm();

  const allCompanies = useCompaniesStore((state) => state.allCompanies);
  const fetchAllCompanies = useCompaniesStore((state) => state.fetchAllCompanies);
  useEffect(() => {
    fetchAllCompanies();
  }, [fetchAllCompanies]);
  const isSgLinked = !!allCompanies
    .find((c) => c.id === companyId)
    ?.origin?.includes("v5");

  const queryClient = useQueryClient();
  const [transferTarget, setTransferTarget] = useState<Device | null>(null);
  const [transferPending, setTransferPending] = useState(false);
  const [bulkTransferOpen, setBulkTransferOpen] = useState(false);

  const handleTransferClick = useCallback((device: Device) => {
    setTransferTarget(device);
  }, []);

  const handleTransferConfirm = useCallback(
    async (
      toCompanyId: string,
      backup: TransferBackup,
      statusPolicy: TransferStatusPolicy,
    ) => {
      const dev = transferTarget;
      if (!dev) return;
      setTransferPending(true);
      try {
        const result = await transferDevice(
          dev.id,
          toCompanyId,
          backup,
          statusPolicy,
        );
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["devices"] }),
          queryClient.invalidateQueries({ queryKey: ["counts"] }),
          queryClient.invalidateQueries({ queryKey: ["resellerCounts"] }),
          queryClient.invalidateQueries({ queryKey: ["vehicles"] }),
          queryClient.invalidateQueries({ queryKey: ["trailers"] }),
          queryClient.invalidateQueries({ queryKey: ["truckYardCounts"] }),
        ]);
        await fetchDevicesFromAPI(undefined, true)
          .then((list) => {
            if (list) useVehiclesSocketStore.getState().refreshDevicesData(list);
          })
          .catch(() => undefined);
        setTransferTarget(null);

        const warnings = [
          result?.backupError && t("devices.transfer.backupFailed"),
          result?.sgFailed && t("devices.transfer.sgFailed"),
          result?.cleanupFailedCount
            ? t("devices.transfer.cleanupFailed", {
                count: result.cleanupFailedCount,
              })
            : null,
        ].filter(Boolean) as string[];

        const unassignedCount = result?.unassigned
          ? Object.values(result.unassigned).reduce(
              (acc, list) => acc + (list?.length || 0),
              0,
            )
          : 0;

        const summary = unassignedCount
          ? t("devices.transfer.unassignedSummary", { count: unassignedCount })
          : "";

        await Swal.fire({
          icon: warnings.length ? "warning" : "success",
          title: t("devices.transfer.done"),
          text: [...warnings, summary].filter(Boolean).join(" "),
        });
      } catch (err) {
        await Swal.fire({
          icon: "error",
          title: t("devices.transfer.errorTitle"),
          text: getError(err),
        });
      } finally {
        setTransferPending(false);
      }
    },
    [transferTarget, queryClient, t],
  );

  const themeClasses: ThemeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
    cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
    buttonText: isDarkMode ? "text-gray-400" : "text-gray-600",
    buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
  };

  const totalPages = meta?.totalPages || 1;

  // Multi-select functionality
  const {
    selectedIds,
    isSelectionMode,
    toggleSelection,
    selectAll,
    clearSelection,
    toggleSelectionMode,
    selectedCount,
  } = useMultiSelect<Device>();

  const handleToggleSelection = useCallback((device: Device) => {
    toggleSelection(device.id);
  }, [toggleSelection]);

  const handleBulkTransferConfirm = useCallback(
    async (
      toCompanyId: string,
      backup: TransferBackup,
      statusPolicy: TransferStatusPolicy,
    ) => {
      setTransferPending(true);
      try {
        const result = await transferDevicesBulk(
          [...selectedIds],
          toCompanyId,
          backup,
          statusPolicy,
        );
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["devices"] }),
          queryClient.invalidateQueries({ queryKey: ["counts"] }),
          queryClient.invalidateQueries({ queryKey: ["resellerCounts"] }),
          queryClient.invalidateQueries({ queryKey: ["vehicles"] }),
          queryClient.invalidateQueries({ queryKey: ["trailers"] }),
          queryClient.invalidateQueries({ queryKey: ["truckYardCounts"] }),
        ]);
        await fetchDevicesFromAPI(undefined, true)
          .then((list) => {
            if (list) useVehiclesSocketStore.getState().refreshDevicesData(list);
          })
          .catch(() => undefined);

        setBulkTransferOpen(false);
        clearSelection();

        await Swal.fire({
          icon: result?.failed ? "warning" : "success",
          title: t("devices.transfer.done"),
          text: t("devices.transfer.bulk.result", {
            transferred: result?.transferred ?? 0,
            failed: result?.failed ?? 0,
          }),
        });
      } catch (err) {
        await Swal.fire({
          icon: "error",
          title: t("devices.transfer.errorTitle"),
          text: getError(err),
        });
      } finally {
        setTransferPending(false);
      }
    },
    [selectedIds, queryClient, clearSelection, t],
  );

  const bulkActions: BulkAction[] = isReseller
    ? [
        {
          id: "transfer",
          label: t("devices.actions.transfer"),
          icon: "ri-arrow-left-right-line",
          variant: "primary",
          onClick: () => setBulkTransferOpen(true),
          disabled: selectedCount === 0,
        },
      ]
    : [];

  return (
    <div className={`h-full flex flex-col`}>
      {/* Alert */}
      {alert.show && (
        <CustomAlert
          title={alert.title}
          text={alert.message}
          icon={alert.type}
        />
      )}

      {/* Loading Overlay for Actions */}
      {loadingAction && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-900/40 backdrop-blur-sm animate-fadeIn">
          <div
            className={`flex flex-col items-center gap-4 rounded-2xl px-10 py-8 shadow-2xl ring-1 animate-scaleIn ${
              isDarkMode
                ? "bg-dark-card ring-white/10"
                : "bg-white ring-black/5"
            }`}
          >
            <span className="relative inline-flex h-12 w-12">
              <span className="absolute inset-0 rounded-full border-4 border-primary-500/20" />
              <span className="absolute inset-0 rounded-full border-4 border-transparent border-t-primary-500 animate-spin" />
            </span>
            <div className="text-center">
              <p className={`text-sm font-semibold ${themeClasses.text}`}>
                Procesando...
              </p>
              <p className={`mt-1 text-xs ${themeClasses.textSecondary}`}>
                Esto puede tardar unos segundos
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <DeviceHeader
        themeClasses={themeClasses}
        isDarkMode={isDarkMode}
        selectedStatus={selectedStatus}
        selectedType={selectedType}
        searchQuery={searchQuery}
        onStatusChange={handleStatusChange}
        onTypeChange={handleTypeChange}
        onSearchChange={handleSearchChange}
        selectedView={selectedView}
        setSelectedView={setSelectedView}
        onAddDevice={openAddModal}
        t={t}
        isSelectionMode={isSelectionMode}
        onToggleSelectionMode={toggleSelectionMode}
        onSelectAll={() => selectAll(devices)}
      />

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-3 sm:p-6">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          </div>
        ) : (
          <>
            <DeviceList
              devices={devices}
              themeClasses={themeClasses}
              isDarkMode={isDarkMode}
              selectedView={selectedView}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onEdit={handleEditClick}
              onDetails={handleDetailsClick}
              onActivate={handleActivateDevice}
              onDeactivate={handleDeactivateDevice}
              onDelete={isReseller ? handleDeleteDevice : undefined}
              onTransfer={isReseller ? handleTransferClick : undefined}
              t={t}
              isSelectionMode={isSelectionMode}
              selectedIds={selectedIds}
              onToggleSelection={handleToggleSelection}
            />

            {devices.length > 0 && (
              <DevicePagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                t={t}
              />
            )}
          </>
        )}
      </div>

      {/* Details Modal */}
      {isModalOpen && modalType === "details" && selectedDevice && (
        <DeviceDetailsModal
          device={selectedDevice}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          onClose={closeModal}
          onEdit={handleEditClick}
          allBrands={allBrands}
          t={t}
        />
      )}

      {/* Edit Modal */}
      {isModalOpen && modalType === "edit" && selectedDevice && (
        <DeviceEditModal
          device={selectedDevice}
          editForm={editForm}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          availableVehicles={availableVehicles}
          availableVehiclesLoading={availableVehiclesLoading}
          isSubmitting={isSubmitting}
          onClose={closeModal}
          onSave={handleSaveChanges}
          onInputChange={handleInputChange}
          allBrands={allBrands}
          allBrandsLoading={allBrandsLoading}
          // onRefreshVehicles={refreshAvailableVehicles} // Comentado para uso futuro
          t={t}
          isSgLinked={isSgLinked}
        />
      )}

      {/* Add Device Modal */}
      {isAddDeviceModalOpen && (
        <DeviceAddModal
          formData={newDeviceForm}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          availableVehicles={availableVehicles}
          availableVehiclesLoading={availableVehiclesLoading}
          availableTrailers={availableTrailers}
          availableTrailersLoading={availableTrailersLoading}
          companyId={companyId}
          isSubmitting={isSubmitting}
          onClose={closeAddModal}
          onSave={handleAddDevice}
          allBrands={allBrands}
          allBrandsLoading={allBrandsLoading}
          isSgLinked={isSgLinked}
        />
      )}

      {transferTarget && (
        <DeviceTransferModal
          device={transferTarget}
          companies={allCompanies}
          currentCompanyId={companyId}
          isDarkMode={isDarkMode}
          themeClasses={themeClasses}
          onClose={() => !transferPending && setTransferTarget(null)}
          onConfirm={handleTransferConfirm}
          isPending={transferPending}
          t={t}
        />
      )}

      {bulkTransferOpen && (
        <DeviceBulkTransferModal
          count={selectedCount}
          companies={allCompanies}
          currentCompanyId={companyId}
          isDarkMode={isDarkMode}
          themeClasses={themeClasses}
          onClose={() => !transferPending && setBulkTransferOpen(false)}
          onConfirm={handleBulkTransferConfirm}
          isPending={transferPending}
          t={t}
        />
      )}

      {/* Bulk Action Bar */}
      {bulkActions.length > 0 && (
        <BulkActionBar
          selectedCount={selectedCount}
          actions={bulkActions}
          onClearSelection={clearSelection}
          isDarkMode={isDarkMode}
        />
      )}
    </div>
  );
}
